import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'

import { useRootStore } from '~/store/RootStore'
import { checkMfaBeforeLoad } from '~/utils/beforeLoad'
import { IPlanItems } from '~/types'
import {
  renderTypicalEveningSpeed,
  setServiceListsItems,
} from '~/utils/service'
import { displayFormatDate, formatBytes, formatCurrency } from '~/utils/helpers'
import { Constants } from '~/utils/Constants'

import {
  Button,
  Card,
  ContentWrapper,
  Flex,
  Grid,
  Spacer,
} from '~/components/elements'
import { ArrowBackIosOutlineIcon } from '~/components/icons'
import Loading from '~/components/loading'
import { PlanDisplay } from '~/components/plan/PlanDisplay'
import { ContactInfo } from '~/components/plan/ContactInfo'
import { ChangePlan } from '~/components/plan/ChangePlan'

const PlanPage = observer(() => {
  const navigate = useNavigate({ from: '/home' })
  const { account, service } = useRootStore()
  const currentService = account.getCurrentService

  const [currentPlan, setCurrentPlan] = useState<any>(null)
  const [planLists, setPlanLists] = useState<IPlanItems[]>([])
  const [serviceList, setServiceLists] = useState<IPlanItems[]>([])
  const [option, setOption] = useState<'' | 'changeplan' | 'cancelplan'>('')

  useEffect(() => {
    if (currentService) {
      setServiceLists(setServiceListsItems(currentService))
      getContract()
    }
  }, [currentService])

  const getContract = async () => {
    if (currentService.contractCode) {
      await service
        .getContractByCode(
          currentService.contractCode,
          currentService.type.toLowerCase()
        )
        .then((contract) => {
          if (contract) {
            setCurrentPlan(contract)
            setItems(contract)
          }
        })
    }
  }

  const setItems = (plan: any) => {
    if (!plan) return
    const itemsToDisplay: IPlanItems[] = []

    itemsToDisplay.push({
      label: 'Name',
      value: currentService.contractName,
    })
    itemsToDisplay.push({
      label: 'Contract Start Date',
      value: displayFormatDate(currentService.contractStartDate),
    })
    itemsToDisplay.push({
      label: 'Contract Length',
      value:
        currentService.contractLength === 0
          ? 'No Contract'
          : `${currentService.contractLength} Months`,
    })

    if (currentService.type.toLowerCase() !== 'voip') {
      itemsToDisplay.push({
        label: 'Speed',
        value: currentService.contractSpeed,
      })
      itemsToDisplay.push({
        label: 'Typical Evening Speeds (Download/Upload)',
        value: renderTypicalEveningSpeed(plan),
      })
    }

    const recurrOrders = recurringOrders()
    if (recurrOrders.length > 0) {
      itemsToDisplay.concat(recurrOrders)
    }

    if (currentService.dataAllowance) {
      itemsToDisplay.push({
        label: 'Data',
        value: formatBytes(currentService.dataAllowance),
      })
    }

    if (currentService.isDataOnly === false && 'Unlimited') {
      itemsToDisplay.push({
        label: 'National Calls',
        value: 'Yes',
      })
      itemsToDisplay.push({
        label: 'National SMS',
        value: 'Yes',
      })
    }
    if (currentService.internationalCallAllowance) {
      itemsToDisplay.push({
        label: 'International Call Allowance',
        value: formatCurrency(currentService.internationalCallAllowance),
      })
    }

    itemsToDisplay.push({
      label: 'Monthly Cost',
      value: currentService.servicePrice
        ? formatCurrency(currentService.servicePrice)
        : '--',
    })

    const recurrCredits = getRecurringCreditItems()
    if (recurrCredits.length > 0) {
      itemsToDisplay.concat(recurrCredits)
    }

    if (Constants.WHOLESALER.settings.code === 'EA') {
      itemsToDisplay.push({
        label: 'Monthly Payable',
        value: currentService.servicePayable
          ? formatCurrency(currentService.servicePayable)
          : '--',
      })
    }

    setPlanLists(itemsToDisplay)
  }

  const recurringOrders = () => {
    const items = currentService.recurringOrders
      ? currentService.recurringOrders.map((element: any) => ({
        label: element.name,
        value: null !== element.amount ? formatCurrency(element.amount) : 0,
      }))
      : []

    return items
  }

  const getRecurringCreditItems = () => {
    return currentService.recurringCredits
      ? currentService.recurringCredits.map((element: any) => ({
        label: element.description,
        value: null !== element.amount ? formatCurrency(element.amount) : 0,
      }))
      : []
  }

  return (
    <ContentWrapper>
      <Button
        size="small"
        variant="secondary"
        onClick={() => navigate({ to: '/home' })}
      >
        <ArrowBackIosOutlineIcon /> Back
      </Button>
      <Spacer height="24px" />
      <Grid columns="minmax(35%, 40%) 1fr" columnsmobile="1fr" gap="24px">
        <div>
          <Card heading="Your Plan">
            {planLists.length == 0 ? (
              <Loading iconwidth="90px" position="static" />
            ) : (
              <PlanDisplay items={planLists} />
            )}
            <Spacer height="12px" />
            <Flex alignitems="center" justifycontent="flex-end" gap="12px">
              <Button
                variant="success"
                onClick={() => setOption('changeplan')}
                disabled={option === 'changeplan'}
              >
                Change Plan
              </Button>
              <Button
                variant="danger"
                onClick={() => setOption('cancelplan')}
                disabled={option === 'cancelplan'}
                title={`To cancel your plan please call us on ${Constants.WHOLESALERSETTINGS.contactSupport}`}
              >
                Cancel Plan
              </Button>
            </Flex>
          </Card>
          <Card heading="Your Service">
            {serviceList.length == 0 ? (
              <Loading iconwidth="90px" position="static" />
            ) : (
              <PlanDisplay items={serviceList} />
            )}
          </Card>
        </div>
        <div>
          {option === 'cancelplan' && (
            <ContactInfo
              heading="Plan Cancellation"
              message="To cancel your plan please call us on"
            />
          )}
          {option === 'changeplan' && (
            <ChangePlan
              currentPlan={currentPlan}
              serviceId={currentService.id}
              serviceCode={currentService.code}
              serviceClass={currentService.serviceClass}
              serviceType={currentService.type}
              onCancel={() => setOption('')}
            />
          )}
        </div>
      </Grid>
    </ContentWrapper>
  )
})

export const Route = createFileRoute('/_authenticated/home/plan')({
  beforeLoad: checkMfaBeforeLoad,
  component: PlanPage,
})
